import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import api from "@helpers/api";
import { postStore } from "@helpers/posts";
import { useCommunity } from "@contexts/CommunityContext";

import { STEPS } from "../enums/steps";
import { defaultActivity } from "../enums/exerciseOptions";
import { DEFAULT_VISIBILITY, getVisibilityOptions } from "../enums/visibilityOptions";
import { readPreferences, writePreferences } from "../helpers/preferences";
import { DEFAULT_NOTIFY } from "../enums/notifyOptions";
import {
    SCHEDULE_MODES,
    SCHEDULE_NOW,
    defaultScheduled,
    isInFuture,
    isoToLocal,
    localToIso,
    scheduledAt,
} from "../helpers/schedule";

// Groups the user can post to: every interactive group of a community they
// own or co-create. Paginated envelope — one page is plenty for a picker.
const GROUPS_URL =
    "community/group/getGroupsAvailableForPosting?page=1&per_page=100";

const STAFF_ONLY = "only_admins";

// Case- and accent-insensitive haystack/needle form for the picker search.
function normalize(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/\p{M}/gu, "")
        .toLowerCase()
        .trim();
}

// `api` resolves with the axios error instead of throwing.
function ok(res) {
    return !!res && !res.isAxiosError && res.status >= 200 && res.status < 300;
}

function managerOf(community) {
    if (!community) return false;
    return typeof community.isAdmin === "function"
        ? !!community.isAdmin()
        : !!community.isAdmin;
}

export default function usePublishPost({ modalRef }) {
    const { t } = useTranslation("groupModals");
    const { communities } = useCommunity();

    // Form fields write straight into this ref; the field components seed
    // their local state from it on mount, so it survives stepping back/forth.
    const configRef = useRef({});
    const callbackRef = useRef();

    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(STEPS.GROUP);
    const [selection, setSelection] = useState({ community: null, group: null });

    // The post being edited (a scheduled one), or null when composing.
    const [editing, setEditing] = useState(null);

    // `null` = not fetched yet (drives the skeleton); `[]` = fetched, empty.
    const [groups, setGroups] = useState(null);
    const [loadingGroups, setLoadingGroups] = useState(false);

    // Picker filters: free text over group + community name, and a set of
    // community ids (empty = every community).
    const [search, setSearch] = useState("");
    const [communityFilter, setCommunityFilter] = useState([]);

    const [visibility, setVisibilityState] = useState(DEFAULT_VISIBILITY);
    const [loading, setLoading] = useState(false);
    const [exerciseRegistry, setExerciseRegistry] = useState(false);
    const [uploadBusyCount, setUploadBusyCount] = useState(0);

    // Managers only: when to publish ({ mode, value }) and the member e-mail.
    const [schedule, setScheduleState] = useState(SCHEDULE_NOW);
    const [notifyMembers, setNotifyMembers] = useState(false);

    // Which footer option panel is open: "visibility" | "schedule" | null.
    const [activePopper, setActivePopper] = useState(null);

    const handleUploadBusyChange = useCallback((uploading) => {
        setUploadBusyCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    }, []);

    modalRef.current = {
        /**
         * @param {{ group?: string, community?: object, post?: object }} preFill
         *   A preselected `group` (id) + its `community` (the Group page) skips
         *   the picker and opens on the form; without one (the sidebar) the
         *   picker shows first. A `post` (a scheduled one) opens the same form
         *   in edit mode: text, image, visibility, moment and notify pre-filled.
         * @param {(post, selection) => void} callback  fired after a successful publish / save
         */
        open(preFill, callback) {
            const { community, group, post, ...rest } = preFill || {};

            const groupId = post?.group?.id || group;

            configRef.current = post
                ? { text: post.text || "", photo: post.image || null, activity: { ...defaultActivity } }
                : { ...rest, activity: { ...defaultActivity } };
            callbackRef.current = callback;

            const preselected = groupId
                ? community?.groups?.find((g) => g.id === groupId) || { id: groupId, ...(post?.group || {}) }
                : null;

            // A new post starts from the last visibility chosen in this browser;
            // an edit starts from the post itself.
            const saved = post ? {} : readPreferences();
            const knownVisibility = getVisibilityOptions(t).some((o) => o.value === saved.visibility);

            setEditing(post || null);
            setSelection({ community: community || null, group: preselected });
            setStep(preselected ? STEPS.POST : STEPS.GROUP);
            setGroups(null);
            setSearch("");
            setCommunityFilter([]);
            setVisibilityState(post?.visibility || (knownVisibility ? saved.visibility : DEFAULT_VISIBILITY));
            setExerciseRegistry(false);
            setUploadBusyCount(0);
            // The schedule is never remembered: a new post always starts on
            // "publish now"; an edit shows the post's own moment.
            setScheduleState(
                post?.scheduled_to ? scheduledAt(isoToLocal(post.scheduled_to)) : SCHEDULE_NOW
            );
            // A new post e-mails the members by default; an edit keeps the post's choice.
            setNotifyMembers(post ? !!post.notify_members : DEFAULT_NOTIFY);
            setActivePopper(null);
            setOpen(true);
        },
    };

    // Fetch lazily, the first time the picker step is shown per opening.
    useEffect(() => {
        if (!open || step !== STEPS.GROUP || groups !== null) return;

        let cancelled = false;
        setLoadingGroups(true);

        api.get(GROUPS_URL)
            .then(({ data }) => {
                if (!cancelled) setGroups(Array.isArray(data?.data) ? data.data : []);
            })
            .catch(() => {
                if (!cancelled) setGroups([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingGroups(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, step, groups]);

    // Every group with its community attached (resolved from the context, a
    // bare `{ id }` when the context doesn't know it).
    const groupItems = useMemo(() => {
        if (!groups) return [];

        const byId = new Map((communities || []).map((c) => [String(c.id), c]));

        return groups.map((group) => ({
            ...group,
            community: byId.get(String(group.community_id)) || { id: group.community_id },
        }));
    }, [groups, communities]);

    // Filter pills: the communities present in the list, in the sidebar's
    // order so the row reads like the navigation (unknown ones last).
    const filterCommunities = useMemo(() => {
        const present = new Map();
        for (const group of groupItems) {
            const key = String(group.community.id);
            if (!present.has(key)) present.set(key, group.community);
        }

        const ordered = [];
        for (const community of communities || []) {
            const key = String(community.id);
            if (!present.has(key)) continue;
            ordered.push(present.get(key));
            present.delete(key);
        }

        return [...ordered, ...present.values()];
    }, [groupItems, communities]);

    const filteredGroups = useMemo(() => {
        const needle = normalize(search);

        return groupItems.filter((group) => {
            if (
                communityFilter.length &&
                !communityFilter.includes(String(group.community.id))
            ) {
                return false;
            }
            if (!needle) return true;
            return (
                normalize(group.name).includes(needle) ||
                normalize(group.community.name).includes(needle)
            );
        });
    }, [groupItems, search, communityFilter]);

    function toggleCommunityFilter(communityId) {
        const key = String(communityId);
        setCommunityFilter((current) =>
            current.includes(key)
                ? current.filter((id) => id !== key)
                : [...current, key]
        );
    }

    function clearCommunityFilter() {
        setCommunityFilter([]);
    }

    function clearGroupFilters() {
        setSearch("");
        setCommunityFilter([]);
    }

    function handleSelectGroup(group) {
        setSelection({ community: group.community, group });
        setStep(STEPS.POST);
    }

    function goToGroupStep() {
        if (editing) return;
        setActivePopper(null);
        setStep(STEPS.GROUP);
    }

    function goToPostStep() {
        if (selection.group) setStep(STEPS.POST);
    }

    // Scheduling and the member e-mail are honoured by the API for the
    // community's owner/creator only, so the controls show for them alone.
    const isManager = managerOf(selection.community);
    // Staff-only posts never e-mail (the API forces it off too).
    const notifyAvailable = visibility !== STAFF_ONLY;
    // "Schedule" chosen = scheduled, even while its date is still empty or
    // past: submit refuses it then, rather than silently publishing now.
    const isScheduled = isManager && schedule.mode === SCHEDULE_MODES.SCHEDULED;

    // Visibility persists per browser while composing; an edit reflects one
    // post. The schedule is deliberately not persisted (see `open`).
    function setVisibility(value) {
        setVisibilityState(value);
        if (!editing) writePreferences({ visibility: value });
    }

    // Back to "now" keeps the moment picked so far, so flipping to "schedule"
    // again within this post restores it instead of the default chip.
    function setScheduleNow() {
        setScheduleState((current) => ({ ...SCHEDULE_NOW, value: current.value }));
    }

    // Selecting "schedule" keeps a still-future moment already chosen, else
    // starts from the first suggestion.
    function setScheduleOn() {
        if (schedule.mode === SCHEDULE_MODES.SCHEDULED) return;
        setScheduleState(isInFuture(schedule.value) ? scheduledAt(schedule.value) : defaultScheduled(t));
    }

    function setScheduleValue(value) {
        setScheduleState(scheduledAt(value));
    }

    // Land on the group the post went to: the community page reads `?group`
    // (see useGroup) and selects that tab, whether it mounts fresh or is
    // already showing this community.
    function openPublishedGroup(groupId) {
        const slug = selection.community?.slug;
        if (!slug) return;

        // Already on this community: swap the entry instead of stacking a
        // duplicate of the same page in the history.
        const path = `/${slug}`;
        window.navigate(`${path}?group=${encodeURIComponent(groupId)}`, {
            replace: window.location.pathname === path,
        });
    }

    function buildPayload() {
        const payload = {
            text: configRef.current.text,
            image: configRef.current.photo,
            visibility,
        };

        if (!editing && exerciseRegistry) {
            payload.activity = {
                duration: configRef.current.activity.duration,
                intensity: configRef.current.activity.intensity,
                mode: configRef.current.activity.mode,
            };
        }

        if (isManager) {
            if (isScheduled) payload.scheduled_to = localToIso(schedule.value);
            // Editing a scheduled post with "now" chosen publishes it (the API's `null`).
            else if (editing) payload.scheduled_to = null;
            payload.notify_members = notifyAvailable && notifyMembers;
        }

        return payload;
    }

    // The API answers 422 with a field → messages map; `post` is its "already
    // published" refusal, `scheduled_to` a moment that is no longer ahead,
    // `text`/`image` an empty post. Server messages are English-only, so the
    // user always gets a catalogue string.
    function reportValidationError(res) {
        const errors = res?.response?.data?.errors || {};
        if (errors.post) return window.setSnackbar(t("publishPost.errors.alreadyPublished"));
        if (errors.scheduled_to) return window.setSnackbar(t("publishPost.errors.scheduleInPast"));
        if (errors.text || errors.image) return window.setSnackbar(t("publishPost.errors.messageRequired"));
        return window.setSnackbar(t("publishPost.errors.saveFailed"));
    }

    async function handleSubmit(event) {
        const groupId = selection.group?.id;
        if (!groupId) return;

        // Mirrors the API rule (text of 2+ characters, or a photo).
        const text = String(configRef.current.text || "").trim();
        if (!configRef.current.photo && text.length < 2) {
            window.setSnackbar(t("publishPost.errors.messageRequired"));
            return;
        }

        if (isScheduled && !isInFuture(schedule.value)) {
            window.setSnackbar(t("publishPost.errors.scheduleInPast"));
            // Put the date inputs in front of the author right away. The click
            // must not reach the document, or an already open schedule panel
            // would read it as a click-away and close.
            event?.stopPropagation?.();
            setActivePopper("schedule");
            return;
        }

        setActivePopper(null);
        setLoading(true);

        try {
            if (editing) {
                const res = await api.patch(
                    `community/group/post/${editing.id}`,
                    buildPayload()
                );

                if (ok(res) && res.data?.id) {
                    postStore.set(res.data);
                    window.setSnackbar(t("publishPost.editSuccess"));
                    await callbackRef.current?.(res.data, selection);
                    setOpen(false);
                } else if (res?.response?.status === 422) {
                    reportValidationError(res);
                } else {
                    window.setSnackbar(t("publishPost.errors.saveFailed"));
                }
            } else {
                const res = await api.post(
                    `community/group/${groupId}/post`,
                    buildPayload()
                );

                if (ok(res) && res.data?.id) {
                    await callbackRef.current?.(res.data, selection);
                    setOpen(false);
                    openPublishedGroup(groupId);
                } else if (res?.response?.status === 422) {
                    reportValidationError(res);
                } else {
                    window.setSnackbar(t("publishPost.errors.saveFailed"));
                }
            }
        } catch (e) {
            window.setSnackbar(t("publishPost.errors.saveFailed"));
        }

        setLoading(false);
    }

    function handleClose() {
        setActivePopper(null);
        setOpen(false);
    }

    return {
        open,
        step,
        selection,
        editing,
        picker: {
            loading: loadingGroups || groups === null,
            groups: filteredGroups,
            total: groupItems.length,
            communities: filterCommunities,
            search,
            communityFilter,
            onSearchChange: setSearch,
            onToggleCommunity: toggleCommunityFilter,
            onClearCommunities: clearCommunityFilter,
            onClearFilters: clearGroupFilters,
            onSelect: handleSelectGroup,
        },
        visibility,
        setVisibility,
        loading,
        configRef,
        exerciseRegistry,
        setExerciseRegistry,
        uploadBusy: uploadBusyCount > 0,
        handleUploadBusyChange,
        isManager,
        schedule,
        isScheduled,
        setScheduleNow,
        setScheduleOn,
        setScheduleValue,
        notifyAvailable,
        notifyMembers,
        setNotifyMembers,
        activePopper,
        setActivePopper,
        goToGroupStep,
        goToPostStep,
        handleSubmit,
        handleClose,
    };
}
